/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  useCallback,
  useEffect,
  useRef,
  type FunctionComponent,
  type JSX
} from "react";
import {
  useBlocker,
  useLocation,
  type BlockerFunction
} from "react-router-dom";

import {
  isPlayerHostingUrl,
  LocationUrls,
  urlToScreenName
} from "../../viewModel/navigation";
import { NetworkGuard } from "../../model/interactor";
import { Log } from "../../model/logging";
import { useUnityDispatch, useUnitySelector } from "../../viewModel";
import { selectIsSignInErrorOverlayActive } from "../../viewModel/signIn";
import { selectIsProfileSelectionRequired } from "../../viewModel/profiles";
import { hidePlayback } from "../../viewModel/videoPlayerUi";
import { isBackStopNavigationState } from "../hooks/useBrowserBackStop";
import {
  setLocationUrl,
  setActiveScreen,
  syncScreenFromUrl,
  pushNavigation,
  replaceNavigation,
  popNavigation
} from "../../viewModel/screens";

const TBlockerState = {
  blocked: "blocked",
  unblocked: "unblocked"
} as const;
type TBlockerState = (typeof TBlockerState)[keyof typeof TBlockerState];

const THistoryAction = {
  push: "PUSH",
  replace: "REPLACE",
  pop: "POP"
} as const;
type THistoryAction = (typeof THistoryAction)[keyof typeof THistoryAction];

/**
 * Returns true when the target path is an internal app route
 * (starts with "/" and is not a full external URL).
 */
function isInternalRoute(path: string): boolean {
  return path.startsWith("/");
}

/**
 * Returns true for the same-path back-stop entry pushed by
 * `useBrowserBackStop`. A POP carrying that state is browser Back onto the
 * entry and is not a back-stop push.
 */
function isBackStopPush(
  historyAction: THistoryAction,
  locationState: unknown
): boolean {
  return (
    historyAction === THistoryAction.push &&
    isBackStopNavigationState(locationState)
  );
}

function shouldBlockProfileSelectionNavigation(
  isProfileSelectionRequired: boolean,
  currentPath: string
): boolean {
  return (
    isProfileSelectionRequired && currentPath === LocationUrls.SELECT_PROFILE
  );
}

/**
 * NavigationBlocker — centralized navigation side-effect handler.
 *
 * Intercepts EVERY navigation event (programmatic, browser back/forward,
 * deep-link) via `useBlocker`. On each blocked navigation it:
 *
 *   0. Prevents duplicate navigation — if the target path matches the
 *      current location, the blocker is reset without side-effects.
 *   0b. Holds the navigation while a sign-in error overlay owns the screen
 *      (see below), taking no side-effect and leaving the blocker blocked.
 *   1. Cancels all in-flight API requests via NetworkGuard (only for
 *      internal routes — skipped when navigating to an external site).
 *   2. Dispatches setLocationUrl (shifts currentUrl → previousUrl).
 *   3. Dispatches setActiveScreen + syncScreenFromUrl (screenController
 *      for the D-pad NavigationEngine).
 *   4. Updates the navigation stack based on the router's historyAction
 *      (PUSH/REPLACE → push; POP → pop). The historyAction is captured
 *      from the BlockerFunction callback args, which is the only place
 *      react-router exposes it — the Blocker object itself does not
 *      include it.
 *   5. Puts the player into PlayerViewMode.Hidden when the destination screen
 *      has no player surface (anything other than /player and /guide), so
 *      playback pauses and disappears instead of leaking audio or video behind
 *      the new screen. Screens that do host a player claim a visible view mode
 *      on mount, which resumes playback where it left off.
 *   6. Calls blocker.proceed() to commit the navigation.
 *
 * This guarantees that browser back/forward triggers identical cleanup
 * to programmatic navigation, preventing stale API responses from
 * updating Redux state after a screen change.
 *
 * ## Holding navigation under a sign-in error overlay (UAW-2415)
 *
 * A sign-in error overlay must stay the authoritative UI until the sign-in
 * flow itself resolves it. Nothing in the app used to guarantee that: overlay
 * lifetime and navigation were independent, so a startup chain settling
 * (`SplashScreen`), a post-sign-in sequence resolving, or an auth-state change
 * flipping a route guard could transition the screen underneath a visible
 * overlay. Worse, `setActiveScreen` below fires at navigation time while the
 * outgoing screen is still mounted; that screen's own cleanup then clears
 * `activeScreen` and the destination route is lazily loaded, so the overlay's
 * ModalController was left with no modal stack to sit on — input-dead — for as
 * long as the chunk took to arrive.
 *
 * Every one of those transitions is a router navigation, and every router
 * navigation lands here, so the invariant is enforced in this one place: while
 * `selectIsSignInErrorOverlayActive` is true the blocked navigation is *held*,
 * not dropped. React Router keeps the blocker in `blocked` state with the
 * pending location, and `proceed()` re-issues that exact navigation, so when
 * the overlay is dismissed this effect re-runs and the navigation completes on
 * its own. No side-effect runs while held: `activeScreen` keeps naming the
 * screen that is actually mounted, which is what keeps the overlay attached
 * and taking D-pad input.
 *
 * Deliberately keyed on the overlay's owner rather than on "an overlay is
 * visible": only the sign-in error overlays hold navigation. Retry and Exit
 * are unaffected — they run through `ConnectionRetryOverlayBridge` and the
 * existing retry/exit mechanisms, and both end by clearing the overlay, which
 * releases the hold.
 *
 * Must be rendered inside the router context. Renders nothing.
 */
const NavigationBlocker: FunctionComponent = (): JSX.Element | null => {
  const TAG: string = "NavigationInterceptor";
  const dispatch = useUnityDispatch();
  const location = useLocation();

  // Drives the hold below. Subscribed to rather than read once, so the effect
  // re-runs — and releases the pending navigation — the moment it goes false.
  const isSignInErrorOverlayActive: boolean = useUnitySelector(
    selectIsSignInErrorOverlayActive
  );
  const isProfileSelectionRequired: boolean = useUnitySelector(
    selectIsProfileSelectionRequired
  );

  // Capture the historyAction from the BlockerFunction callback.
  // The BlockerFunction is the ONLY API that exposes the navigation action
  // (POP, PUSH, REPLACE). We store it in a ref so the effect can read it.
  const historyActionRef = useRef<THistoryAction>(THistoryAction.push);

  // ─── Initial page load seeding ───
  // useBlocker does NOT fire on the first render (there is no navigation to
  // intercept). Seed activeScreen, currentUrl, and navigationStack from the
  // current location once on mount so the slice is never stale on cold start
  // or hard-refresh.
  const initializedRef = useRef<boolean>(false);
  useEffect((): void => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      const initialPath: string = location.pathname;
      if (isInternalRoute(initialPath)) {
        const initialScreen: string = urlToScreenName(initialPath);
        dispatch(setLocationUrl(initialPath));
        dispatch(setActiveScreen(initialScreen));
        dispatch(syncScreenFromUrl(initialPath));
        dispatch(pushNavigation(initialPath));
      }
    }
  }, [dispatch, location.pathname]);

  const shouldBlock: BlockerFunction = useCallback(
    ({ historyAction }): boolean => {
      historyActionRef.current = historyAction;
      return true;
    },
    []
  );

  const blocker = useBlocker(shouldBlock);

  useEffect((): void => {
    if (blocker.state === TBlockerState.blocked) {
      const nextPath: string = blocker.location.pathname;

      // ─── Prevent duplicate navigation ───
      // If the target path is identical to the current location, reset the
      // blocker without triggering any side-effects (no network cancellation,
      // no Redux updates, no navigation stack pollution).
      //
      // The one exception is the back-stop entry `useBrowserBackStop` pushes
      // on a root screen (UAW-1129): it is a deliberate same-path PUSH, and
      // is committed without side-effects — the screen is not changing, so
      // its in-flight requests, the nav stack and the player are left alone.
      // Browser Back onto it is then a same-path POP, which the reset below
      // swallows; that is what makes Back a no-op there.
      if (nextPath === location.pathname) {
        if (isBackStopPush(historyActionRef.current, blocker.location.state)) {
          Log.d(TAG, `Back-stop entry committed at ${nextPath}`);
          blocker.proceed();
          return;
        }
        Log.d(TAG, `Duplicate navigation blocked — already at ${nextPath}`);
        blocker.reset();
        return;
      }

      // ─── Hold while a sign-in error overlay owns the screen ───
      // Return without proceeding and without any side-effect: the blocker
      // stays in "blocked" state holding `nextPath`, so nothing is lost. This
      // effect re-runs when the overlay clears (it depends on the flag) and
      // proceeds with the same pending navigation then. reset() would be
      // wrong — it discards the navigation instead of deferring it.
      if (isSignInErrorOverlayActive) {
        Log.d(
          TAG,
          `Navigation to ${nextPath} held — sign-in error overlay is active`
        );
        return;
      }

      if (
        shouldBlockProfileSelectionNavigation(
          isProfileSelectionRequired,
          location.pathname
        )
      ) {
        Log.d(
          TAG,
          `Navigation to ${nextPath} ignored — profile selection is required`
        );
        blocker.reset();
        return;
      }

      // Only run cleanup and state updates for internal app routes.
      // External navigations (Redirects to another domain)
      // should not cancel in-flight requests or mutate Redux state.
      if (isInternalRoute(nextPath)) {
        const nextScreen: string = urlToScreenName(nextPath);

        // 1. Cancel stale network requests
        NetworkGuard.getInstance().cancelAllForNavigation();

        // 2. Sync URL + active screen + screenController into Redux
        dispatch(setLocationUrl(nextPath));
        dispatch(setActiveScreen(nextScreen));
        dispatch(syncScreenFromUrl(nextPath));

        // 3. Update the navigation stack based on the captured historyAction.
        //    REPLACE overwrites the top entry rather than stacking one: it adds
        //    no browser-history entry, so pushing would leave the stack — and
        //    every selector derived from it — claiming a previous screen that
        //    navigate(-1) cannot reach.
        if (historyActionRef.current === THistoryAction.pop) {
          dispatch(popNavigation());
        } else if (historyActionRef.current === THistoryAction.replace) {
          dispatch(replaceNavigation(nextPath));
        } else {
          dispatch(pushNavigation(nextPath));
        }

        // 4. Hide the player when the destination has no player surface.
        // Done here — before blocker.proceed() and therefore before the
        // outgoing screen unmounts — so its cleanup sees the Hidden mode and
        // skips its own atmospheric switch (which stops the stream on desktop).
        // Screens that DO host a player claim their own visible view mode on
        // mount, which restores visibility and resumes playback.
        if (!isPlayerHostingUrl(nextPath)) {
          dispatch(hidePlayback());
        }
      }

      // 5. Allow the navigation to proceed
      blocker.proceed();
    }
  }, [
    blocker,
    dispatch,
    location.pathname,
    isProfileSelectionRequired,
    isSignInErrorOverlayActive
  ]);

  return null;
};

export default NavigationBlocker;
